import * as vp from 'vopay';

import { FunctionComponent, useContext, useMemo } from 'react';
import TicketCreationContext from './TicketCommentCreationContext';
import { DialogueHandler, TicketCommentCreationDialogueHandlers } from '../types';
import styles from './TicketComments.module.scss';
import CashManagement from './svgs/CashManagement.svg';
import DetailedReporting from './svgs/DetailedReporting.svg';
import IonCamera from './svgs/IonCamera.svg';
import { useTranslation } from 'react-i18next';

const { ConfirmationDialogue, Box, Modal, Button } = vp;

type TicketCommentCreationDialogueProps = {
  handleDialogue: DialogueHandler<TicketCommentCreationDialogueHandlers>;
};

type FC = FunctionComponent<TicketCommentCreationDialogueProps>;

const TicketCreationDialogue: FC = ({ handleDialogue }: TicketCommentCreationDialogueProps) => {
  const {
    showTicketCommentCreateSuccessAlert,
    showTicketCommentCreateErrorAlert,
    ticketCommentCreateError,
    showTicketTips,
  } = useContext(TicketCreationContext);
  const { t } = useTranslation('translations', { keyPrefix: 'Tickets.Details.Dialog' });

  return useMemo(
    () => (
      <div>
        <ConfirmationDialogue
          isVisible={showTicketCommentCreateErrorAlert}
          title={t('CommentWasNotAdded.title')}
          status="error"
          subTitle={`${ticketCommentCreateError}`}
          confirm={{
            label: t('CommentWasNotAdded.confirmButton'),
            action: () => handleDialogue({ type: 'create', state: 'fail' }),
          }}
        />

        <ConfirmationDialogue
          isVisible={showTicketCommentCreateSuccessAlert}
          status="success"
          title={t('CommentSuccessfullySubmitted.title')}
          subTitle={t('CommentSuccessfullySubmitted.subTitle')}
          confirm={{
            label: t('CommentSuccessfullySubmitted.confirmButton'),
            action: () => handleDialogue({ type: 'create', state: 'success' }),
          }}
        />

        <Modal isVisible={showTicketTips} enableBgClick={false}>
          <div className={styles.tipsModal}>
            <Box width="100%" jc="sb" ai="center" pb="lg" mb="sm">
              <h2 className={styles.tipsHeading}>{t('TipsModal.title')}</h2>
              <div className={styles.closeButton}>
                <Button
                  id="confirmationDialogue-close"
                  iconLocation="center"
                  iconSize="xlg"
                  color="secondary"
                  label=""
                  icon="cross"
                  height="12.5px"
                  width="12.5px"
                  iconWidth="12px"
                  iconHeight="12px"
                  textSize={12}
                  fontUnit="px"
                  noShadow
                  noPadding
                  onClick={() => {
                    handleDialogue({ type: 'tips', state: 'success' });
                  }}
                />
              </div>
            </Box>
            <Box flx="col" gapSize="50px">
              <Box gapSize="24px" ai="center">
                <img src={DetailedReporting} alt="" style={{ width: '112px', height: '112px' }} />
                <Box flx="col">
                  <div className={styles.tipsHeader}>{t('TipsModal.DetailedReporting.title')}</div>
                  <div className={styles.tipsDescription}>{t('TipsModal.DetailedReporting.description')}</div>
                </Box>
              </Box>
              <Box gapSize="24px" ai="center">
                <img src={CashManagement} alt="" style={{ width: '112px', height: '112px' }} />
                <Box flx="col">
                  <div className={styles.tipsHeader}>{t('TipsModal.CashManagement.title')}</div>
                  <div className={styles.tipsDescription}>{t('TipsModal.CashManagement.description')}</div>
                </Box>
              </Box>
              <Box gapSize="24px" ai="center">
                <img src={IonCamera} alt="" style={{ width: '112px', height: '112px' }} />
                <Box flx="col">
                  <div className={styles.tipsHeader}>{t('TipsModal.IonCamera.title')}</div>
                  <div className={styles.tipsDescription}>{t('TipsModal.IonCamera.description')}</div>
                </Box>
              </Box>
            </Box>
          </div>
        </Modal>
      </div>
    ),
    [showTicketCommentCreateSuccessAlert, showTicketCommentCreateErrorAlert, showTicketTips],
  );
};

export default TicketCreationDialogue;
